import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "create-page" }
const _hoisted_2 = { class: "create-card" }
const _hoisted_3 = { class: "form-row" }
const _hoisted_4 = { class: "form-col" }
const _hoisted_5 = { class: "field-hint" }
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "upload-placeholder"
}
const _hoisted_8 = { class: "form-col" }
const _hoisted_9 = { class: "battery-level-container" }
const _hoisted_10 = { class: "battery-bar-bg" }
const _hoisted_11 = { class: "battery-pct" }
const _hoisted_12 = { class: "zone-info" }
const _hoisted_13 = { class: "zone-title-row" }
const _hoisted_14 = { class: "zone-hint" }
const _hoisted_15 = { class: "form-actions-footer" }
const _hoisted_16 = { class: "action-hint" }
const _hoisted_17 = { class: "buttons" }
const _hoisted_18 = { class: "wialon-dialog-content" }
const _hoisted_19 = { class: "wialon-dialog-content" }
const _hoisted_20 = {
  key: 0,
  class: "zone-empty-hint"
}

import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Right,
  Camera,
  Link,
  ArrowRight,
  InfoFilled,
  Connection,
  Search,
} from '@element-plus/icons-vue'
import { useGolfVehiclesStore } from '@/store/golf-vehicles'
import { routeNames } from '@/router/route-names'
import { useGolfHeaderStore } from '@/store/golf-header'
import { useGolfI18n } from '@/composables/useGolfI18n'
import { getGolfAreas, type GolfArea } from '@/api/golf-areas'


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateModel',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const golfStore = useGolfVehiclesStore()
const headerStore = useGolfHeaderStore()
const { t } = useGolfI18n()

const formRef = ref<any>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const isLoading = ref(false)
const showIotSync = ref(false)
const wialonDialogVisible = ref(false)
const wialonSearch = ref('')

const zoneDialogVisible = ref(false)
const zoneSearch = ref('')
const zones = ref<GolfArea[]>([])
const zonesLoading = ref(false)

const previewUrl = ref<string>('')
const selectedFile = ref<File | null>(null)
const batteryLevel = ref(0)

const isEdit = computed(() => !!route.query.id)
const assetId = computed(() => route.query.id as string)

const formData = reactive({
  asset_number: '',
  profile_id: '',
  status: 'active',
  wialon_unit_id: '' as string | number,
  wialon_unit_name: '',
  zone_id: '' as string,
})

const filteredWialonUnits = computed(() => {
  const units = golfStore.wialonUnits || []
  if (!wialonSearch.value) return units
  const search = wialonSearch.value.toLowerCase()
  return units.filter(
    u =>
      u.name.toLowerCase().includes(search) ||
      String(u.id)
        .toLowerCase()
        .includes(search)
  )
})

const openWialonDialog = () => {
  wialonDialogVisible.value = true
  golfStore.fetchWialonUnits()
}

const selectWialonUnit = (unit: any) => {
  formData.wialon_unit_id = unit.id
  formData.wialon_unit_name = unit.name
  wialonDialogVisible.value = false
}

const disconnectWialon = () => {
  formData.wialon_unit_id = ''
  formData.wialon_unit_name = ''
}

const selectedZone = computed(() =>
  zones.value.find(zone => zone.id === formData.zone_id) || null
)

const filteredZones = computed(() => {
  if (!zoneSearch.value) return zones.value
  const search = zoneSearch.value.toLowerCase()
  return zones.value.filter(zone => zone.name.toLowerCase().includes(search))
})

const loadZones = async () => {
  zonesLoading.value = true
  try {
    const response = await getGolfAreas()
    const allAreas = response.data.data.data || []
    zones.value = allAreas.filter(zone => zone.type === 'parking')
  } catch (e) {
    console.error('Failed to load deployment zones:', e)
  } finally {
    zonesLoading.value = false
  }
}

const openZoneDialog = () => {
  zoneDialogVisible.value = true
  if (!zones.value.length) loadZones()
}

const selectZone = (zone: GolfArea) => {
  if (!zone.is_active && zone.id !== formData.zone_id) return
  formData.zone_id = zone.id
  zoneDialogVisible.value = false
}

const clearZone = () => {
  formData.zone_id = ''
}

const formRules = {
  asset_number: [
    { required: true, message: 'Model ID is required', trigger: 'blur' },
  ],
  profile_id: [
    { required: true, message: 'Please select a Make', trigger: 'change' },
  ],
}

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handlePhotoUpload = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    selectedFile.value = target.files[0]
    previewUrl.value = URL.createObjectURL(selectedFile.value)
  }
}

const submitForm = async () => {
  if (!formRef.value) return

  await formRef.value.validate(async (valid: boolean) => {
    if (!valid) return

    isLoading.value = true
    try {
      if (isEdit.value) {
        // Update Asset
        const updated = await golfStore.updateAsset(assetId.value, {
          asset_number: formData.asset_number,
          status: formData.status, // status can be modified in Edit mode
          wialon_unit_id: formData.wialon_unit_id
            ? String(formData.wialon_unit_id)
            : null,
          wialon_unit_name: formData.wialon_unit_name
            ? formData.wialon_unit_name
            : null,
          zone_id: formData.zone_id || null,
        })

        if (updated) {
          if (selectedFile.value) {
            const imageFormData = new FormData()
            imageFormData.append('image', selectedFile.value)
            imageFormData.append('type', 'front')
            imageFormData.append('sort_order', '0')
            await golfStore.createImage(formData.profile_id, imageFormData)
          }

          ElMessage.success(t('golf.fleet.model_updated_success'))
          router.push({ name: routeNames.golfCars, query: { tab: 'models' } })
        }
      } else {
        // Create Asset
        const newAsset = await golfStore.createAsset(formData.profile_id, {
          asset_number: formData.asset_number,
          wialon_unit_id: formData.wialon_unit_id
            ? String(formData.wialon_unit_id)
            : null,
          wialon_unit_name: formData.wialon_unit_name
            ? formData.wialon_unit_name
            : null,
          zone_id: formData.zone_id || null,
        })

        if (newAsset) {
          if (selectedFile.value) {
            const imageFormData = new FormData()
            imageFormData.append('image', selectedFile.value)
            imageFormData.append('type', 'front')
            imageFormData.append('sort_order', '0')
            await golfStore.createImage(formData.profile_id, imageFormData)
          }

          ElMessage.success(t('golf.fleet.model_created_success'))
          router.push({ name: routeNames.golfCars, query: { tab: 'models' } })
        }
      }
    } catch (e) {
      console.error('Error saving model:', e)
    } finally {
      isLoading.value = false
    }
  })
}

const goBack = () => {
  router.push({ name: routeNames.golfCars, query: { tab: 'models' } })
}

onMounted(async () => {
  headerStore.setHeaderInfo(
    isEdit.value || route.query.from_trip
      ? `${t('golf.fleet.title')} › ${t('golf.common.edit')} ${t('golf.fleet.model_name')}`
      : `${t('golf.fleet.title')} › ${t('golf.fleet.add_model')}`,
    false
  )

  if (!golfStore.profiles.length) {
    await golfStore.fetchProfiles()
  }

  loadZones()

  if (route.query.profile_id) {
    formData.profile_id = route.query.profile_id as string
  }

  if (isEdit.value) {
    let foundAsset: any = null
    let foundProfile: any = null
    for (const profile of golfStore.profiles) {
      const asset = profile.assets?.find((a: any) => a.id === assetId.value)
      if (asset) {
        foundAsset = asset
        foundProfile = profile
        formData.profile_id = profile.id
        break
      }
    }

    if (foundAsset) {
      formData.asset_number = foundAsset.asset_number || ''
      formData.status = foundAsset.status || 'active'
      formData.wialon_unit_id = foundAsset.wialon_unit_id || ''
      formData.wialon_unit_name = foundAsset.wialon_unit_name || ''
      formData.zone_id = foundAsset.zone_id || ''
      batteryLevel.value = foundAsset.battery_pct || 0

      if (
        foundProfile &&
        foundProfile.images &&
        foundProfile.images.length > 0
      ) {
        previewUrl.value =
          foundProfile.images[0].secure_url || foundProfile.images[0].url
      }
    }
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_form = _resolveComponent("el-form")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_el_form, {
        ref_key: "formRef",
        ref: formRef,
        model: formData,
        rules: formRules,
        "label-position": "top",
        "hide-required-asterisk": "",
        onSubmit: _withModifiers(submitForm, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.model_id'),
                prop: "asset_number",
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: formData.asset_number,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.asset_number) = $event)),
                    placeholder: _ctx.$t('golf.fleet.eg_gc001'),
                    clearable: "",
                    maxlength: "50"
                  }, null, 8, ["modelValue", "placeholder"]),
                  _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t('golf.fleet.unique_chassis_hint')), 1)
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.assigned_make'),
                prop: "profile_id",
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_select, {
                    modelValue: formData.profile_id,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.profile_id) = $event)),
                    placeholder: _ctx.$t('golf.fleet.select_from_catalog'),
                    style: {"width":"100%"}
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(golfStore).profiles, (profile) => {
                        return (_openBlock(), _createBlock(_component_el_option, {
                          key: profile.id,
                          label: profile.name,
                          value: profile.id
                        }, null, 8, ["label", "value"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue", "placeholder"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.profile_photo'),
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: "upload-box",
                    onClick: triggerFileInput
                  }, [
                    (previewUrl.value)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: previewUrl.value,
                          class: "preview-img"
                        }, null, 8, _hoisted_6))
                      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createVNode(_component_el_icon, { size: 24 }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Camera))
                            ]),
                            _: 1
                          }),
                          _createElementVNode("span", null, [
                            _createTextVNode(_toDisplayString(_ctx.$t('golf.fleet.drop_image')) + " ", 1),
                            _createElementVNode("b", null, _toDisplayString(_ctx.$t('golf.fleet.browse')), 1)
                          ])
                        ])),
                    _createElementVNode("input", {
                      type: "file",
                      ref_key: "fileInputRef",
                      ref: fileInputRef,
                      class: "hidden-input",
                      accept: "image/*",
                      onChange: handlePhotoUpload
                    }, null, 544)
                  ])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.battery'),
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", {
                        class: "battery-bar-fill",
                        style: _normalizeStyle({ width: batteryLevel.value + '%' })
                      }, null, 4)
                    ]),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(batteryLevel.value) + "%", 1)
                  ])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.telemetry_data'),
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: _normalizeClass(["iot-connected-box", { 'is-connected': !!formData.wialon_unit_id }]),
                    onClick: openWialonDialog
                  }, [
                    (formData.wialon_unit_id)
                      ? (_openBlock(), _createBlock(_component_el_icon, { key: 0 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Link))
                          ]),
                          _: 1
                        }))
                      : (_openBlock(), _createBlock(_component_el_icon, { key: 1 }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(Connection))
                          ]),
                          _: 1
                        })),
                    _createTextVNode(" " + _toDisplayString(formData.wialon_unit_id
                    ? `${_ctx.$t(
                        'golf.fleet.connected_status'
                      )} ${formData.wialon_unit_name ||
                        formData.wialon_unit_id}`
                    : _ctx.$t('golf.fleet.click_to_connect')), 1)
                  ], 2),
                  (formData.wialon_unit_id)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "disconnect-action",
                        onClick: disconnectWialon
                      }, _toDisplayString(_ctx.$t('golf.fleet.disconnect')), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.common.status'),
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_select, {
                    modelValue: formData.status,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.status) = $event)),
                    placeholder: _ctx.$t('golf.common.select'),
                    style: {"width":"100%"}
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_option, {
                        label: _ctx.$t('golf.common.active'),
                        value: "active"
                      }, null, 8, ["label"]),
                      _createVNode(_component_el_option, {
                        label: _ctx.$t('golf.fleet.status_inactive'),
                        value: "inactive"
                      }, null, 8, ["label"])
                    ]),
                    _: 1
                  }, 8, ["modelValue", "placeholder"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(_component_el_form_item, {
                label: _ctx.$t('golf.fleet.resort_deployment_zone'),
                class: "form-item"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: "zone-box",
                    onClick: openZoneDialog
                  }, [
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "zone-map-preview" }, null, -1)),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("span", {
                          class: _normalizeClass(["dot", selectedZone.value ? 'green-dot' : 'orange-dot'])
                        }, null, 2),
                        _createElementVNode("b", null, _toDisplayString(selectedZone.value
                        ? selectedZone.value.name
                        : _ctx.$t('golf.fleet.no_zone_assigned')), 1)
                      ]),
                      _createElementVNode("div", _hoisted_14, _toDisplayString(selectedZone.value
                        ? _ctx.$t('golf.fleet.change_deployment_zone')
                        : _ctx.$t('golf.fleet.click_select_zone')), 1)
                    ]),
                    _createVNode(_component_el_icon, { class: "arrow-icon" }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(ArrowRight))
                      ]),
                      _: 1
                    })
                  ]),
                  (formData.zone_id)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "disconnect-action",
                        onClick: _withModifiers(clearZone, ["stop"])
                      }, _toDisplayString(_ctx.$t('golf.fleet.clear_zone_assignment')), 1))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"])
            ])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("div", _hoisted_16, [
              _createVNode(_component_el_icon, null, {
                default: _withCtx(() => [
                  _createVNode(_unref(InfoFilled))
                ]),
                _: 1
              }),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('golf.fleet.deployment_logs_hint')), 1)
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createVNode(_component_el_button, {
                class: "cancel-btn",
                size: "large",
                onClick: goBack
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('golf.common.cancel')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_el_button, {
                class: "submit-btn",
                type: "warning",
                size: "large",
                loading: isLoading.value,
                onClick: submitForm
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('golf.common.save')) + " ", 1),
                  _createVNode(_component_el_icon, { class: "submit-icon" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Right))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["loading"])
            ])
          ])
        ]),
        _: 1
      }, 8, ["model"])
    ]),
    _createVNode(_component_el_dialog, {
      modelValue: wialonDialogVisible.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((wialonDialogVisible).value = $event)),
      title: _ctx.$t('golf.fleet.connect_wialon_dialog'),
      width: "500px",
      class: "wialon-dialog"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_18, [
          _createVNode(_component_el_input, {
            modelValue: wialonSearch.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((wialonSearch).value = $event)),
            placeholder: _ctx.$t('golf.fleet.search_wialon_placeholder'),
            "prefix-icon": "Search",
            clearable: "",
            class: "wialon-search"
          }, null, 8, ["modelValue", "placeholder"]),
          _withDirectives((_openBlock(), _createBlock(_component_el_table, {
            data: filteredWialonUnits.value,
            style: {"width":"100%"},
            height: "300",
            class: "wialon-table"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                prop: "id",
                label: _ctx.$t('golf.fleet.unit_id'),
                width: "100"
              }, null, 8, ["label"]),
              _createVNode(_component_el_table_column, {
                prop: "name",
                label: _ctx.$t('golf.common.name')
              }, null, 8, ["label"]),
              _createVNode(_component_el_table_column, {
                label: _ctx.$t('golf.trips.actions'),
                width: "100",
                align: "right"
              }, {
                default: _withCtx(({ row }) => [
                  _createVNode(_component_el_button, {
                    class: _normalizeClass(["wialon-select-btn", {
                  'is-selected': row.id === Number(formData.wialon_unit_id),
                }]),
                    size: "small",
                    disabled: 
                  row.is_linked && row.id !== Number(formData.wialon_unit_id)
                ,
                    onClick: ($event: any) => (selectWialonUnit(row))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(row.id === Number(formData.wialon_unit_id)
                    ? 'Selected'
                    : row.is_linked
                    ? 'Linked'
                    : 'Select'), 1)
                    ]),
                    _: 2
                  }, 1032, ["class", "disabled", "onClick"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }, 8, ["data"])), [
            [_directive_loading, _unref(golfStore).isLoading]
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"]),
    _createVNode(_component_el_dialog, {
      modelValue: zoneDialogVisible.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((zoneDialogVisible).value = $event)),
      title: "Select Resort Deployment Zone",
      width: "500px",
      class: "zone-dialog"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_19, [
          _createVNode(_component_el_input, {
            modelValue: zoneSearch.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((zoneSearch).value = $event)),
            placeholder: "Search by zone name...",
            "prefix-icon": "Search",
            clearable: "",
            class: "zone-search"
          }, null, 8, ["modelValue"]),
          _withDirectives((_openBlock(), _createBlock(_component_el_table, {
            data: filteredZones.value,
            style: {"width":"100%"},
            height: "300",
            class: "zone-table"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_table_column, {
                prop: "name",
                label: "Zone Name"
              }),
              _createVNode(_component_el_table_column, {
                label: "Status",
                width: "100"
              }, {
                default: _withCtx(({ row }) => [
                  _createVNode(_component_el_tag, {
                    type: row.is_active ? 'success' : 'info',
                    size: "small"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(row.is_active ? 'Active' : 'Inactive'), 1)
                    ]),
                    _: 2
                  }, 1032, ["type"])
                ]),
                _: 1
              }),
              _createVNode(_component_el_table_column, {
                label: "Action",
                width: "100",
                align: "right"
              }, {
                default: _withCtx(({ row }) => [
                  _createVNode(_component_el_button, {
                    class: _normalizeClass(["zone-select-btn", { 'is-selected': row.id === formData.zone_id }]),
                    size: "small",
                    disabled: !row.is_active && row.id !== formData.zone_id,
                    onClick: ($event: any) => (selectZone(row))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(row.id === formData.zone_id ? 'Selected' : 'Select'), 1)
                    ]),
                    _: 2
                  }, 1032, ["class", "disabled", "onClick"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["data"])), [
            [_directive_loading, zonesLoading.value]
          ]),
          (!zonesLoading.value && !zones.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, " No deployment zones have been created yet. Ask an admin to add one under Map › Parking Areas. "))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})